import React, { useState, useCallback, useRef, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SnackBar from "../../../components/SnackBar";
import { listarEmpresas } from "../../../services/api-empresas.service";
import { criarNovaPreviaInss } from "../../../services/api-inss.service";
import GenericDatePickerReference from "../../../components/GenericDatePickerReference";
import { formatValue } from "../../../utils/formatValue";
import { Chip } from "@mui/material";
import { debounce } from "lodash";
import styles from "./styles.module.css";

const LIMITE_BUSCA_EMPRESAS = 25;
const LIMITE_INICIAL_EMPRESAS = 100;
const ANOS_PERIODO_PADRAO = 5;

/**
 * Retorna o período padrão: últimos 5 anos a partir de hoje (data inicial = hoje - 5 anos, data final = hoje).
 * Cálculo exato: mesmo dia e mês, 5 anos atrás.
 */
function getPeriodoPadrao5Anos() {
  const hoje = new Date();
  const dtFinal = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());
  const dtInicial = new Date(hoje.getFullYear() - ANOS_PERIODO_PADRAO, hoje.getMonth(), hoje.getDate());
  return { dtInicial, dtFinal };
}

/**
 * Formata resposta da API para opções do Autocomplete
 */
function formatarOpcoesEmpresas(empresasFormatadas) {
  return (empresasFormatadas || [])
    .map((emp) => {
      const cnpj = (emp.cnpj || "").replace(/\D/g, "");
      const razaoSocial = emp.razao_social || emp.razaoSocial || emp.nome || "";
      return {
        id: emp.id_empresa ?? emp.id ?? cnpj,
        cnpj,
        razao_social: razaoSocial,
        label: razaoSocial,
        is_matriz: !!emp.is_matriz,
      };
    })
    .filter((emp) => emp.cnpj && emp.razao_social && emp.is_matriz);
}

/**
 * Modal para criar nova prévia de INSS
 * Busca empresas sob demanda (busca assíncrona com debounce), igual à listagem de empresas.
 */
const ModalNovaPrevia = ({
  open,
  onClose,
  onConfirm,
  categorias = [],
  rechargeData,
}) => {
  const [opcoesEmpresas, setOpcoesEmpresas] = useState([]);
  const [empresaSelecionada, setEmpresaSelecionada] = useState(null);
  const [loadingEmpresas, setLoadingEmpresas] = useState(false);
  const [loading, setLoading] = useState(false);
  const [periodo, setPeriodo] = useState(() => getPeriodoPadrao5Anos());
  const [categoriaSelecionada, setCategoriaSelecionada] = useState(null);
  const [dtInicioEsocial, setDtInicioEsocial] = useState(null);
  const [isCPRB, setIsCPRB] = useState(false);
  const [objSnackBar, setObjSnackBar] = useState({ type: "", msg: "" });
  const [inputBuscaEmpresa, setInputBuscaEmpresa] = useState("");
  const buscaEmpresasAbortRef = useRef(null);

  const buscarEmpresas = useCallback(async (busca, limit = LIMITE_BUSCA_EMPRESAS) => {
    if (buscaEmpresasAbortRef.current) buscaEmpresasAbortRef.current.abort();
    buscaEmpresasAbortRef.current = new AbortController();
    setLoadingEmpresas(true);
    try {
      const params = { limit, offset: 0 };
      if (busca != null && String(busca).trim() !== "") {
        params.q = String(busca).trim();
      }
      const response = await listarEmpresas(params);
      const dados = response?.dados != null ? response.dados : Array.isArray(response) ? response : [];
      setOpcoesEmpresas(formatarOpcoesEmpresas(dados));
    } catch (err) {
      if (err?.name === "AbortError") return;
      console.error("Erro ao buscar empresas:", err);
      setObjSnackBar({ type: "error", msg: "Erro ao carregar empresas. Tente novamente." });
      setOpcoesEmpresas([]);
    } finally {
      setLoadingEmpresas(false);
      buscaEmpresasAbortRef.current = null;
    }
  }, []);

  const buscarEmpresasDebounced = useRef(
    debounce((busca) => {
      buscarEmpresas(busca, LIMITE_BUSCA_EMPRESAS);
    }, 350)
  ).current;

  const handleOpenEmpresas = useCallback(() => {
    if (opcoesEmpresas.length === 0 && !loadingEmpresas) {
      buscarEmpresas(inputBuscaEmpresa, LIMITE_INICIAL_EMPRESAS);
    }
  }, [inputBuscaEmpresa, opcoesEmpresas.length, loadingEmpresas, buscarEmpresas]);

  const handleInputChangeEmpresas = useCallback(
    (event, value, reason) => {
      setInputBuscaEmpresa(value ?? "");
      if (reason === "input") {
        buscarEmpresasDebounced(value ?? "");
      }
    },
    [buscarEmpresasDebounced]
  );

  useEffect(() => {
    if (open) {
      setPeriodo(getPeriodoPadrao5Anos());
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      buscarEmpresasDebounced.cancel();
      if (buscaEmpresasAbortRef.current) buscaEmpresasAbortRef.current.abort();
      setOpcoesEmpresas([]);
      setInputBuscaEmpresa("");
    }
  }, [open, buscarEmpresasDebounced]);

  const handleEmpresaChange = (event, value) => {
    setEmpresaSelecionada(value);
    setInputBuscaEmpresa(value ? (value.label ?? value.razao_social ?? "") : "");
    setObjSnackBar({ type: "", msg: "" });
  };

  const formatarDataParaAPI = (data) => {
    if (!data) return null;
    const date = new Date(data);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}-01`; // Primeiro dia do mês
  };

  const handleConfirmar = async () => {
    if (!empresaSelecionada) {
      setObjSnackBar({
        type: "error",
        msg: "Selecione uma empresa",
      });
      return;
    }

    if (!periodo.dtInicial || !periodo.dtFinal) {
      setObjSnackBar({
        type: "error",
        msg: "Preencha o período (mês/ano inicial e final).",
      });
      return;
    }

    if (periodo.dtInicial > periodo.dtFinal) {
      setObjSnackBar({
        type: "error",
        msg: "Data inicial não pode ser maior que a data final.",
      });
      return;
    }

    if (!categoriaSelecionada) {
      setObjSnackBar({
        type: "error",
        msg: "Selecione uma categoria tributária.",
      });
      return;
    }

    if (!dtInicioEsocial) {
      setObjSnackBar({
        type: "error",
        msg: "Data de início do eSocial é obrigatória.",
      });
      return;
    }

    try {
      setLoading(true);
      
      const cnpjLimpo = String(empresaSelecionada.cnpj).replace(/\D/g, '');
      
      const payload = {
        cnpj: cnpjLimpo,
        categoria: categoriaSelecionada.id,
        dtInicial: formatarDataParaAPI(periodo.dtInicial),
        dtFinal: formatarDataParaAPI(periodo.dtFinal),
        dtInicioEsocial: formatarDataParaAPI(dtInicioEsocial),
        isCPRB: isCPRB,
        status: "PREVIA-PENDENTE",
      };

      console.log("[Prévia INSS] Criando prévia com payload:", payload);

      const response = await criarNovaPreviaInss(payload);

      console.log("[Prévia INSS] Prévia criada com sucesso:", response);

      setObjSnackBar({
        type: "success",
        msg: `Prévia criada com sucesso para ${empresaSelecionada.razao_social}!`,
      });

      // Recarregar a lista de prévias
      if (rechargeData) {
        await rechargeData();
      }

      handleLimpar();
      onClose();
    } catch (error) {
      console.error("[Prévia INSS] Erro ao criar prévia:", error);
      setObjSnackBar({
        type: "error",
        msg:
          error.response?.data?.mensagem ||
          error.message ||
          "Erro ao criar prévia. Tente novamente.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLimpar = () => {
    setEmpresaSelecionada(null);
    setOpcoesEmpresas([]);
    setInputBuscaEmpresa("");
    setPeriodo({ dtInicial: null, dtFinal: null });
    setCategoriaSelecionada(null);
    setDtInicioEsocial(null);
    setIsCPRB(false);
    setObjSnackBar({ type: "", msg: "" });
  };

  const handleClose = () => {
    handleLimpar();
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>
          <div className={styles.title}>
            <AddIcon sx={{ marginRight: ".5rem" }} />
            Nova Prévia INSS
          </div>
        </DialogTitle>
        <DialogContent className={styles.content}>
          {/* Seleção de Empresa */}
          <div className={styles.field}>
            <Autocomplete
              value={empresaSelecionada}
              onChange={handleEmpresaChange}
              onOpen={handleOpenEmpresas}
              onInputChange={handleInputChangeEmpresas}
              inputValue={inputBuscaEmpresa}
              options={opcoesEmpresas}
              getOptionLabel={(option) => (option && (option.label ?? option.razao_social)) || ""}
              isOptionEqualToValue={(option, value) => option?.cnpj === value?.cnpj}
              filterOptions={(x) => x}
              fullWidth
              size="small"
              loading={loadingEmpresas}
              disabled={loading}
              ListboxProps={{ style: { maxHeight: 320 } }}
              noOptionsText={
                loadingEmpresas ? "Buscando..." : inputBuscaEmpresa.trim() ? "Nenhuma empresa encontrada." : "Digite ou abra a lista para buscar."
              }
              renderOption={(props, option) => (
                <li {...props} key={option.id ?? option.cnpj}>
                  <div style={{ display: "flex", flexDirection: "column", width: "100%", gap: "4px" }}>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {option.razao_social}
                    </span>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                      {option.is_matriz && (
                        <Chip
                          label="Matriz"
                          size="small"
                          sx={{
                            fontSize: "0.7rem",
                            height: "20px",
                            bgcolor: "#e8f5e9",
                            color: "#2e7d32",
                            fontWeight: 600,
                          }}
                        />
                      )}
                      {option.cnpj && (
                        <Chip
                          label={formatValue("cnpj", option.cnpj)}
                          size="small"
                          sx={{
                            fontSize: "0.7rem",
                            height: "20px",
                            width: "fit-content",
                          }}
                        />
                      )}
                    </div>
                  </div>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Empresa"
                  placeholder="Digite para buscar por razão social ou CNPJ"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingEmpresas ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </div>

          {/* Seleção de Categoria */}
          <div className={styles.field}>
            <Autocomplete
              value={categoriaSelecionada}
              onChange={(_, value) => setCategoriaSelecionada(value)}
              options={categorias}
              getOptionLabel={(option) => option.label || ""}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              fullWidth
              size="small"
              disabled={loading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Categoria Tributária"
                  placeholder="Selecione uma categoria"
                />
              )}
            />
          </div>

          {/* Período da prévia */}
          <div className={styles.periodRow}>
            <GenericDatePickerReference
              label="Mês/Ano Inicial"
              value={periodo.dtInicial}
              onChange={(value) =>
                setPeriodo((prev) => ({ ...prev, dtInicial: value }))
              }
              className={styles.periodField}
            />
            <GenericDatePickerReference
              label="Mês/Ano Final"
              value={periodo.dtFinal}
              onChange={(value) =>
                setPeriodo((prev) => ({ ...prev, dtFinal: value }))
              }
              className={styles.periodField}
            />
          </div>

          {/* Data de Início do eSocial */}
          <div className={styles.field}>
            <GenericDatePickerReference
              label="Data de Início do eSocial"
              value={dtInicioEsocial}
              onChange={(value) => setDtInicioEsocial(value)}
            />
          </div>

          {/* Switch CPRB */}
          <div className={styles.field}>
            <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <input
                type="checkbox"
                checked={isCPRB}
                onChange={(e) => setIsCPRB(e.target.checked)}
                disabled={loading}
              />
              <span>CPRB</span>
            </label>
          </div>
        </DialogContent>

        <DialogActions className={styles.containerBtn}>
          <Button
            onClick={handleClose}
            variant="contained"
            className={styles.btnCancelar}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmar}
            variant="contained"
            className={styles.btnSalvar}
            disabled={
              loading ||
              !empresaSelecionada ||
              !periodo.dtInicial ||
              !periodo.dtFinal ||
              !categoriaSelecionada ||
              !dtInicioEsocial
            }
          >
            Criar Prévia
          </Button>
        </DialogActions>
      </Dialog>

      <SnackBar
        objSnackBar={objSnackBar}
        setObjSnackBar={setObjSnackBar}
      />
    </>
  );
};

export default ModalNovaPrevia;
